import React, { useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CustomImageInput from "@/FormInputs/CustomImageInput";
import tokens from "@/theme/tokens";
import { uploadAvatar } from "@/store/avatar-v1/avatarActions";
import emptyAvatarImage from "@/images/empty_profile.png";

export const MAX_AVATAR_BYTES = 10485760;

interface AvatarUploadFormProps {
  renderSaveButton: (image?: File | null) => React.ReactNode;
  skip: () => void;
  avatar?: string;
  isSignup: boolean;
}

const AvatarUploadForm = ({
  renderSaveButton,
  skip,
  avatar = "",
  isSignup,
}: AvatarUploadFormProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["translation.forms"]);

  const { control, handleSubmit, watch } = useForm<{ picture: File | null }>({
    defaultValues: { picture: null },
  });

  const picture = watch("picture");

  // Previously createObjectURL ran inline in the JSX, so every render minted a
  // new blob URL and none were ever revoked.
  const previewUrl = useMemo(
    () => (picture ? window.URL.createObjectURL(picture) : null),
    [picture]
  );
  useEffect(
    () => () => {
      if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    },
    [previewUrl]
  );

  const onSubmit = (values: { picture: File | null }) => {
    const formData = new FormData();
    formData.append("_utf8", "true");
    if (values.picture) {
      formData.append("rebel_profile[avatar]", values.picture);
    }
    dispatch(uploadAvatar(formData, skip));
  };

  return (
    <Box
      component="form"
      encType="multipart/form-data"
      onSubmit={handleSubmit(onSubmit)}
      // CustomImageInput renders the real file input; it stays hidden and is
      // driven by the label below.
      sx={{ "& input#picture": { display: "none" } }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 2, md: 4 }}
        sx={{
          flexWrap: "wrap",
          justifyContent: { xs: "flex-start", md: "space-around" },
          alignItems: { xs: "flex-start", md: "center" },
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              textTransform: "uppercase",
              lineHeight: "90%",
            }}
          >
            {t("AvatarUploadHeadingPart1")}
          </Typography>
          <Typography variant="h1" color="primary">
            {t("Photo")}
          </Typography>
          {/*
            A real MUI Button rather than a styled Box so it picks up the
            theme's button geometry instead of hand-rolled padding. `component`
            keeps it a <label>, which is what drives the hidden file input.
            It stays `small` — this is a secondary picker next to the primary
            submit button, so it should not carry the same weight.
          */}
          <Button
            component="label"
            htmlFor="picture"
            variant="contained"
            size="small"
            // The palette's accent teal lives at tokens.color.accentTeal;
            // `color="info"` would resolve to info.main instead. Hover is
            // pinned to the same value because the old .choose-btn had no
            // hover state.
            sx={{ mt: 1, bgcolor: tokens.color.accentTeal, "&:hover": { bgcolor: tokens.color.accentTeal } }}
          >
            {t("select")}
          </Button>
          <Controller
            name="picture"
            control={control}
            render={({ field }) => (
              <CustomImageInput
                field={{ name: field.name, value: field.value }}
                setFieldValue={(_name: string, value: unknown) =>
                  field.onChange(value)
                }
                imageType="avatar-upload"
              />
            )}
          />
          {picture && picture.size > MAX_AVATAR_BYTES && (
            <Typography sx={{ color: "error.main", mt: 3 }}>
              {t("PhotoLimit")}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            m: { xs: tokens.space.avatarPhotoPreviewMargin, md: tokens.space.avatarUploadPhotoMargin },
            width: 250,
            height: 250,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component="img"
            src={previewUrl || avatar || emptyAvatarImage}
            alt={picture || avatar ? "temp" : "default avatar"}
            sx={{
              height: 200,
              width: "auto",
              objectFit: "cover",
              m: "auto",
              minHeight: 250,
              minWidth: 250,
              display: "block",
              position: "relative",
            }}
          />
        </Box>
      </Stack>

      <Stack
        direction="row"
        sx={{
          width: 1,
          my: tokens.space.px10,
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        {isSignup && (
          <Button
            type="button"
            onClick={skip}
            variant="text"
            sx={{ color: "grey.600", mr: tokens.space.px15 }}
          >
            {t("SkipForNow")} &gt;
          </Button>
        )}
        <div>{renderSaveButton(picture)}</div>
      </Stack>
    </Box>
  );
};

export default AvatarUploadForm;
